import { useState, useRef, useCallback } from 'react'
import { SuperDocEditor as SDEditor } from '@superdoc-dev/react'
import '@superdoc-dev/react/style.css'
import type { SuperDocRef } from '@superdoc-dev/react'

/**
 * Dev-only smoke test for the SuperDoc docx round-trip.
 *
 * Load a docx → inspect in editor → export → compare OOXML with:
 *   node renderer/scripts/compare-docx.js <input.docx> <exported.docx>
 *
 * Guarded by import.meta.env.DEV — renders nothing in production builds.
 */
export default function SmokeTestPage() {
  const [docxBlob, setDocxBlob] = useState<File | null>(null)
  const [status, setStatus] = useState('No file loaded.')
  const ref = useRef<SuperDocRef>(null)

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setDocxBlob(file)
    setStatus(`Loaded: ${file.name} (${(file.size / 1024).toFixed(1)} KB) — check editor below.`)
  }, [])

  const handleExport = useCallback(async () => {
    const instance = ref.current?.getInstance()
    if (!instance) {
      setStatus('Editor not ready — wait for it to finish loading, then try again.')
      return
    }
    setStatus('Exporting…')
    try {
      const result = await instance.export({ exportType: ['docx'], triggerDownload: true })
      const size = result instanceof Blob ? `${(result.size / 1024).toFixed(1)} KB` : 'unknown size'
      setStatus(`Exported (${size}) — file saved to Downloads. Run compare-docx.js to analyse.`)
    } catch (err) {
      setStatus(`Export failed: ${err instanceof Error ? err.message : String(err)}`)
    }
  }, [])

  return (
    <div style={{ padding: '24px', maxWidth: '960px', margin: '0 auto', fontFamily: 'sans-serif' }}>
      <h1 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>
        SuperDoc Docx Round-Trip — Smoke Test
      </h1>
      <p style={{ fontSize: '13px', color: '#666', marginBottom: '20px' }}>
        Dev only. Load a docx → inspect rendering → export → compare OOXML.
        <br />
        Test A: load <code>tempfiles/Sukenik CV - 2026 Product Manager.docx</code> (native Word).
        <br />
        Test B: load <code>renderer/scripts/test-cv-branded.docx</code> (our renderer output).
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <input
          type="file"
          accept=".docx"
          onChange={handleFileChange}
          style={{ fontSize: '13px' }}
        />
        <button
          onClick={handleExport}
          disabled={!docxBlob}
          style={{
            padding: '6px 16px',
            fontSize: '13px',
            background: docxBlob ? '#833c0b' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: docxBlob ? 'pointer' : 'default',
          }}
        >
          Export as DOCX
        </button>
        <span style={{ fontSize: '13px', color: '#444' }}>{status}</span>
      </div>

      {docxBlob && (
        <div
          style={{
            border: '1px solid #ddd',
            borderRadius: '8px',
            overflow: 'hidden',
            height: '80vh',
          }}
        >
          <SDEditor
            key={docxBlob.name + docxBlob.size}
            ref={ref}
            documentMode="editing"
            document={docxBlob}
            hideToolbar={false}
          />
        </div>
      )}
    </div>
  )
}
